// Dialogs, in place of window.prompt and window.confirm.
//
// Those were fine for a prototype and wrong for a tool people rely on: they
// cannot be styled, cannot show a validation message, cannot hold two fields,
// and a "move to folder" built out of them asks you to type the number of a
// folder from a numbered list. Everything destructive or multi-field is a real
// form now.
//
// Escape closes, the backdrop closes, focus moves into the dialog on open and
// back to whatever opened it on close, and Tab is kept inside while it is open.

import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Modal = ({ title, description, onClose, children, footer, wide = false }) => {
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    // The field the dialog is *about* if one says so, otherwise the first one.
    // "Edit tags" and "Rename" open the same form, and landing in the wrong
    // field means typing into the wrong field.
    const panel = panelRef.current;
    const first =
      panel?.querySelector("[data-autofocus]") ??
      panel?.querySelector("input, textarea, select") ??
      panel?.querySelector(FOCUSABLE);
    first?.focus();
    first?.select?.();

    const opener = openerRef.current;
    return () => opener?.focus?.();
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const items = [...(panelRef.current?.querySelectorAll(FOCUSABLE) ?? [])];
      if (items.length === 0) return;
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return createPortal(
    <div
      className="backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      onKeyDown={onKeyDown}
    >
      <div
        className={wide ? "modal wide" : "modal"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {children}
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
};

/**
 * A dialog wrapping a form. Submitting runs `onSubmit`, which may be async and
 * may throw: the message it throws is shown in place rather than swallowed.
 */
export const FormModal = ({
  title,
  description,
  submitLabel = "Save",
  onSubmit,
  onClose,
  children,
  busy = false,
  error = null,
  note = null,
  canSubmit = true,
  wide = false,
}) => (
  <Modal title={title} description={description} onClose={onClose} wide={wide}>
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy && canSubmit) onSubmit();
      }}
    >
      <div className="modal-body">
        {children}
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="modal-foot">
        {note ? <span className="left">{note}</span> : null}
        <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={busy || !canSubmit}>
          {busy ? "Working…" : submitLabel}
        </button>
      </div>
    </form>
  </Modal>
);

/** A destructive confirmation. Named after what it will do, never "OK". */
export const ConfirmModal = ({
  title,
  description,
  confirmLabel = "Delete",
  onConfirm,
  onClose,
  busy = false,
  error = null,
}) => (
  <Modal title={title} description={description} onClose={onClose}>
    <div className="modal-body">
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
    <div className="modal-foot">
      <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="btn danger" onClick={onConfirm} disabled={busy}>
        {busy ? "Working…" : confirmLabel}
      </button>
    </div>
  </Modal>
);
